import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  CircularLoader,
  Drawer,
  Input,
  ShareV2Icon,
  Tab,
  Tabs,
} from "components";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { KbFileDetailResponse } from "shared";
import { KbShareLink } from "./KbShareLink";
import { ChipSelectField } from "components/input/ChipSelectField";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { QuillEditorWithChips } from "components/quill";
import {
  useCheckUserAuth,
  useCreateShareableLink,
  useGetCompanySignature,
  useGetShareableLink,
  useSendKbEmail,
} from "controller";
import toast from "react-hot-toast";
import { useArticleShareUrl } from "../hooks/useArticleShareUrl";

const schema = yup.object({
  subject: yup.string().required("Subject is required").trim(),
  html_body: yup.string().required("Body is required").trim(),
  to: yup
    .array()
    .of(yup.string().email("Please enter a valid email address"))
    .required("Recipients are required")
    .min(1, "At least one recipient is required"),
  cc: yup.array().of(yup.string().email("Please enter a valid email address")),
  add_signature: yup.boolean().default(false),
});

export interface ShareViaEmailTabRef {
  onSubmit: () => void;
  isPending: boolean;
}

export const KbShareButton = ({
  kbFileDetails,
  mode,
}: {
  kbFileDetails: KbFileDetailResponse;
  mode: "edit" | "view";
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [shareLinkMappingId, setShareLinkMappingId] = useState(
    kbFileDetails.shareable_link_id,
  );
  const [tabValue, setTabValue] = React.useState(0);
  const theme = useTheme();
  const emailTabRef = useRef<ShareViaEmailTabRef>(null);
  const { mutateAsync: createShareableLink, isLoading } =
    useCreateShareableLink();
  const { data: userAuthData, isLoading: isUserAuthLoading } =
    useCheckUserAuth(isModalOpen);
  const handleShare = async () => {
    if (
      kbFileDetails?.shareable_link_id &&
      kbFileDetails?.shareable_link_is_active
    ) {
      setIsModalOpen(true);
    } else {
      const link = await createShareableLink(kbFileDetails.article_id);
      setShareLinkMappingId(link?.mapping_id);
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <Stack
        // component={"button"}
        onClick={handleShare}
        sx={{
          width: "40px",
          height: "40px",
          borderRadius: "100%",
          boxShadow: "0px 0px 4px 0px #00000029,0px 0px 2px 0px #00000014",
          border: `1px solid ${theme.palette.neutral["010"]}`,
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#FFF",
          cursor: "pointer",
        }}
      >
        {isLoading ? <CircularLoader size={16} /> : <ShareV2Icon />}
      </Stack>
      {isModalOpen && (
        <Drawer
          title="Share"
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          headerStyles={{
            pb: "0px !important",
          }}
          footer={tabValue === 0 ? <></> : null}
          additionalHeaderComponent={
            <Tabs
              sx={{ paddingLeft: "24px" }}
              value={tabValue}
              onChange={(e, value) => {
                setTabValue(value);
              }}
            >
              {["Share via link", "Share via email"].map((tab) => (
                <Tab key={tab} label={tab} />
              ))}
            </Tabs>
          }
          primaryButtonText={tabValue === 1 ? "Share" : undefined}
          onPrimaryButtonClick={
            tabValue === 1 ? () => emailTabRef.current?.onSubmit() : undefined
          }
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={() => setIsModalOpen(false)}
          isLoading={tabValue === 1 ? emailTabRef.current?.isPending : false}
        >
          <Stack
            sx={{
              width: "520px",
            }}
          >
            {tabValue === 0 ? (
              <ShareViaLinkTab
                kbFileDetails={kbFileDetails}
                userAuthData={userAuthData}
              />
            ) : (
              <ShareViaEmailTab
                ref={emailTabRef}
                kbFileDetails={kbFileDetails}
                onClose={() => setIsModalOpen(false)}
                userAuthData={userAuthData}
                shareLinkId={shareLinkMappingId || ""}
              />
            )}
          </Stack>
        </Drawer>
      )}
    </>
  );
};

const ShareViaLinkTab = ({
  kbFileDetails,
  userAuthData,
}: {
  kbFileDetails: KbFileDetailResponse;
  userAuthData: Record<string, any>;
}) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        p: "24px",
        gap: "8px",
      }}
    >
      <KbShareLink
        articleId={kbFileDetails?.article_id}
        userAuthData={userAuthData}
      />
      <Typography variant="body-small" color={theme.palette.lightBg.low}>
        Anyone with this link can access the article
      </Typography>
    </Stack>
  );
};

const ShareViaEmailTab = forwardRef<
  ShareViaEmailTabRef,
  {
    kbFileDetails: KbFileDetailResponse;
    onClose: () => void;
    userAuthData: Record<string, any>;
    shareLinkId: string;
  }
>(({ kbFileDetails, onClose, userAuthData, shareLinkId }, ref) => {
  const { data: companySignature } = useGetCompanySignature();
  const { mutateAsync: sendEmail, isLoading } = useSendKbEmail();
  const url = useArticleShareUrl(userAuthData, shareLinkId);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    values: {
      subject: kbFileDetails.name,
      html_body: `<p>Hello</p>
      <p>Please find the support documentation here: <a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a></p>`,
    },
    defaultValues: {
      to: [],
      subject: "",
      html_body: "",
      cc: [],
      add_signature: false,
    },
  });

  const watchBody = watch("html_body");
  const onSubmit = handleSubmit(async (data) => {
    try {
      await sendEmail({
        subject: data.subject,
        to: data.to as string[],
        cc: data.cc as string[] | undefined,
        html_body: data.html_body,
        appendSignature: data.add_signature,
      });
      toast.success("Email sent successfully");
      onClose();
    } catch {
      toast.error("Failed to send email");
    }
  });

  useImperativeHandle(ref, () => ({
    onSubmit,
    isPending: isLoading,
  }));

  const watchAddSignature = watch("add_signature");
  const renderedSignature = useMemo(() => {
    if (watchAddSignature) {
      const signatureData = companySignature?.signature_html;

      return (
        <Stack sx={{ width: "317px", height: "100%" }}>
          {signatureData && (
            <Box
              className="content-box"
              sx={{ p: "0px 0px", transition: "filter 0.2s" }}
              dangerouslySetInnerHTML={{
                __html: signatureData,
              }}
            />
          )}
        </Stack>
      );
    }

    return null;
  }, [watchAddSignature, companySignature]);

  return (
    <Stack
      sx={{
        p: "24px",
      }}
    >
      <Input
        name="subject"
        label="Subject"
        error={errors?.subject ? true : false}
        errorMessage={errors?.subject?.message}
        control={control}
        sx={{ mb: "16px" }}
      />
      <ChipSelectField
        name="to"
        label={"To"}
        placeholder={"Enter email address"}
        control={control}
        error={errors.to ? true : false}
        errorMessage={
          errors.to && Array.isArray(errors.to)
            ? errors.to.find((err) => err)?.message || ""
            : errors.to?.message || ""
        }
      />

      <ChipSelectField
        name="cc"
        label={"Cc"}
        placeholder={"Enter email address"}
        control={control}
        error={errors.cc ? true : false}
        errorMessage={
          errors.cc && Array.isArray(errors.cc)
            ? errors.cc.find((err) => err)?.message || ""
            : errors.cc?.message || ""
        }
      />
      <QuillEditorWithChips
        showToolbar={false}
        chipOptions={[]}
        placeholder="Type your Mail Body"
        error={errors?.html_body ? true : false}
        onChange={(value) => {
          setValue("html_body", value);
        }}
        externalChipOptions={[
          {
            id: "signature",
            label: "Add signature",
            value: "signature",
          },
        ]}
        minHeight="250px"
        onExternalChipOptionClick={(value: any) => {
          if (value.value === "signature") {
            setValue("add_signature", true);
          }
        }}
        value={watchBody}
        additionalSignatureComponent={renderedSignature}
      />
    </Stack>
  );
});
